import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import {
  translateText,
  SUPPORTED_LANGUAGES,
  LANGUAGE_FLAGS,
  LANGUAGE_NATIVE_NAMES,
  type LanguageCode,
} from "@/features/chat/services/translationService";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { inquiryService } from "@/features/inquiries/services/inquiryService";
import {
  getPresignedUrl,
  uploadToS3Direct,
  confirmAttachmentInDB,
  deleteAttachmentFromAll,
} from "@/shared/services/uploadService";
import { announcementService } from "@/features/announcements/services/feedService";
import { getMimeType } from "@/shared/utils/fileHelpers";
import { formatDate, formatDateTime, formatDisplayName } from "@/shared/utils/formatters";
import { Input } from "@/shared/components/ui/Input";
import { Button } from "@/shared/components/ui/Button";
import { Badge } from "@/shared/components/ui/Badge";
import { Toast } from "@/shared/components/feedback/Toast";
import { AttachmentDropZone } from "@/features/announcements/components/AttachmentDropZone";
import { AttachmentList } from "@/features/announcements/components/AttachmentList";
import { CommentSection } from "@/features/announcements/components/CommentSection";
import type {
  Inquiry,
  InquiryFormData,
  IssueOption,
  OfficerOption,
} from "@/features/inquiries/types/inquiry";
import type {
  AnnouncementAttachment,
  AttachmentUploadItem,
} from "@/features/announcements/types/feed";

const OFFICER_TYPES = ["officer", "admin"];
const STATUS_OPTIONS = ["New", "In Progress", "Completed"];

const STATUS_DB_MAP: Record<string, string> = {
  New: "NEW",
  "In Progress": "INPROG",
  Completed: "CLOSED",
};

const STATUS_DISPLAY_MAP: Record<string, string> = {
  NEW: "New",
  INPROG: "In Progress",
  CLOSED: "Completed",
};

const ACCEPTED_MIME_TYPES = ["application/pdf", "image/jpeg", "image/png", "image/gif"];
const MAX_FILE_SIZE = 10 * 1024 * 1024;

const statusVariant: Record<string, "default" | "success" | "warning" | "danger" | "info"> = {
  New: "info",
  "In Progress": "warning",
  Completed: "success",
};

type TabId = "info" | "comments" | "files";

const emptyForm: InquiryFormData = {
  title: "",
  description: "",
  type: "",
  occur_date: "",
  high_pri: false,
  watcher: [],
  resolution: "",
  closed_dt: null,
};

interface InquiryDetailPanelProps {
  inquiry: Inquiry | null;
  isOpen: boolean;
  onClose: () => void;
  onRefresh: () => void;
}

export const InquiryDetailPanel: React.FC<InquiryDetailPanelProps> = ({
  inquiry,
  isOpen,
  onClose,
  onRefresh,
}) => {
  const { t, i18n } = useTranslation();
  const { userData } = useAuth();
  const isOfficer = OFFICER_TYPES.includes(userData?.userType?.toLowerCase() || "");

  const [activeTab, setActiveTab] = useState<TabId>("info");
  const [form, setForm] = useState<InquiryFormData>(emptyForm);
  const [initialStatus, setInitialStatus] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Translation state
  const [translatedTitle, setTranslatedTitle] = useState<string | null>(null);
  const [translatedDescription, setTranslatedDescription] = useState<string | null>(null);
  const [translatedResolution, setTranslatedResolution] = useState<string | null>(null);
  const [isTranslating, setIsTranslating] = useState(false);
  const [activeLang, setActiveLang] = useState<LanguageCode | null>(null);
  const [langPickerOpen, setLangPickerOpen] = useState(false);
  const langPickerRef = useRef<HTMLDivElement>(null);

  // Dropdown data
  const [issues, setIssues] = useState<IssueOption[]>([]);
  const [officers, setOfficers] = useState<OfficerOption[]>([]);

  // Watcher selection
  const [watcherDropdownOpen, setWatcherDropdownOpen] = useState(false);
  const [watcherSearch, setWatcherSearch] = useState("");
  const watcherDropdownRef = useRef<HTMLDivElement>(null);

  // Comment count (driven by CommentSection callback)
  const [commentCount, setCommentCount] = useState(0);

  // Attachments
  const [attachments, setAttachments] = useState<AnnouncementAttachment[]>([]);
  const [uploads, setUploads] = useState<AttachmentUploadItem[]>([]);

  const showToast = useCallback((message: string, type: "success" | "error") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  // Load dropdowns
  useEffect(() => {
    inquiryService.getActiveIssues(i18n.language).then(setIssues).catch(console.error);
    if (isOfficer) {
      inquiryService.getOfficersByBU(userData?.businessUnit || "").then(setOfficers).catch(console.error);
    }
  }, [i18n.language, isOfficer, userData?.businessUnit]);

  // Populate form when inquiry changes / panel opens
  useEffect(() => {
    if (!inquiry || !isOpen) return;
    setActiveTab("info");
    setError("");
    setConfirmDelete(false);
    setTranslatedTitle(null);
    setTranslatedDescription(null);
    setTranslatedResolution(null);
    setActiveLang(null);
    setUploads([]);
    setCommentCount(inquiry.comment_count ?? 0);

    const displayStatus = STATUS_DISPLAY_MAP[inquiry.status ?? ""] || inquiry.status || "New";
    setInitialStatus(displayStatus);
    setForm({
      ticket_id: inquiry.ticket_id,
      title: inquiry.title || "",
      description: inquiry.description || "",
      type: inquiry.type || "",
      status: displayStatus,
      occur_date: inquiry.occur_date ? inquiry.occur_date.slice(0, 10) : "",
      high_pri: inquiry.high_pri || false,
      watcher: inquiry.watcher || [],
      owner_id: inquiry.owner_id,
      assigned_to: inquiry.assigned_to,
      resolution: inquiry.resolution || "",
      closed_dt: inquiry.closed_dt ? inquiry.closed_dt.slice(0, 10) : null,
      company: inquiry.company,
      opened_by: inquiry.opened_by,
    });

    const normalised = (inquiry.attachments ?? []).map((a: any) => ({
      ...a,
      display_name: a.display_name || a.name || "",
      file_type: a.file_type || a.type || "",
    }));
    setAttachments(normalised);
  }, [inquiry?.ticket_id, isOpen]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (watcherDropdownRef.current && !watcherDropdownRef.current.contains(e.target as Node))
        setWatcherDropdownOpen(false);
      if (langPickerRef.current && !langPickerRef.current.contains(e.target as Node))
        setLangPickerOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "New": return t("inquiry.status.new");
      case "In Progress": return t("inquiry.status.inprogress");
      case "Completed": return t("inquiry.status.completed");
      default: return status;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === "checkbox") {
      setForm(prev => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
    } else {
      setForm(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleWatcherToggle = (officerId: string) => {
    setForm(prev => {
      const current = prev.watcher || [];
      const updated = current.includes(officerId)
        ? current.filter(w => w !== officerId)
        : [...current, officerId];
      return { ...prev, watcher: updated };
    });
  };

  const validate = (): boolean => {
    if (!form.title.trim()) { setError(t("feed.form.required_fields")); return false; }
    if (!form.type) { setError(t("feed.form.required_fields")); return false; }
    if (form.status === "Completed") {
      if (!form.resolution?.trim()) { setError(t("inquiry.form.resolution")); return false; }
      if (!form.closed_dt) { setError(t("inquiry.form.close_date")); return false; }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate() || !inquiry) return;
    setSaving(true);
    setError("");
    try {
      const payload = {
        ...form,
        status: STATUS_DB_MAP[form.status || "New"] || form.status,
      };
      await inquiryService.updateInquiry(inquiry.ticket_id, payload);
      setInitialStatus(form.status ?? "");
      showToast(t("common.success"), "success");
      onRefresh();
    } catch (err: any) {
      const errMsg = err?.response?.data?.error || t("feed.form.save_error");
      setError(errMsg);
      showToast(errMsg, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!inquiry) return;
    setDeleting(true);
    try {
      await inquiryService.deleteInquiry(inquiry.ticket_id);
      onRefresh();
      onClose();
    } catch (err) {
      console.error("Failed to delete inquiry:", err);
      showToast(t("feed.form.save_error"), "error");
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const handleTranslate = async (targetLang: LanguageCode) => {
    setLangPickerOpen(false);
    setIsTranslating(true);
    try {
      const [titleResult, descResult, resResult] = await Promise.all([
        form.title ? translateText(form.title, targetLang) : Promise.resolve(null),
        form.description ? translateText(form.description, targetLang) : Promise.resolve(null),
        form.resolution ? translateText(form.resolution, targetLang) : Promise.resolve(null),
      ]);
      if (titleResult?.success) setTranslatedTitle(titleResult.translatedText);
      if (descResult?.success) setTranslatedDescription(descResult.translatedText);
      if (resResult?.success) setTranslatedResolution(resResult.translatedText);
      setActiveLang(targetLang);
    } catch (err) {
      console.error("Translation error:", err);
    } finally {
      setIsTranslating(false);
    }
  };

  const handleShowOriginal = () => {
    setTranslatedTitle(null);
    setTranslatedDescription(null);
    setTranslatedResolution(null);
    setActiveLang(null);
  };

  // ── Attachment handlers ──────────────────────────────────────────────────────
  const handleFilesSelected = (files: File[]) => {
    if (!inquiry) return;
    for (const file of files) {
      const mimeType = getMimeType(file.name);
      if (!ACCEPTED_MIME_TYPES.includes(mimeType)) { setError(t("feed.form.invalid_file_type")); continue; }
      if (file.size > MAX_FILE_SIZE) { setError(t("feed.form.file_too_large")); continue; }

      const localId = typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : Date.now().toString() + Math.random().toString(36);

      setUploads(prev => [...prev, { localId, file, status: "uploading", progress: 0 }]);

      (async () => {
        try {
          const { uploadUrl, s3Key, bucketName } = await getPresignedUrl(file.name, mimeType, "inquiries", String(inquiry.ticket_id));
          await uploadToS3Direct(uploadUrl, file, mimeType, (progress) => {
            setUploads(prev => prev.map(u => u.localId === localId ? { ...u, progress } : u));
          });
          const confirmed = await confirmAttachmentInDB({
            relation_type: "inquiries",
            relation_id: String(inquiry.ticket_id),
            s3_key: s3Key,
            s3_bucket: bucketName,
            display_name: file.name,
            file_type: mimeType,
            file_size: file.size,
          });
          setUploads(prev => prev.map(u => u.localId === localId ? { ...u, status: "done", progress: 100, attachment: confirmed } : u));
          setAttachments(prev => [...prev, confirmed]);
          setTimeout(() => setUploads(prev => prev.filter(u => u.localId !== localId)), 1000);
        } catch (err: any) {
          setUploads(prev => prev.map(u => u.localId === localId ? { ...u, status: "error", error: err.message } : u));
        }
      })();
    }
  };

  const handleAttachmentDelete = async (attachmentId: number | string) => {
    try {
      await deleteAttachmentFromAll(attachmentId);
      setAttachments(prev => prev.filter(a => a.attachment_id !== attachmentId));
    } catch (err) {
      console.error("Failed to delete attachment:", err);
    }
  };

  const handleAttachmentRename = async (attachmentId: number | string, newName: string) => {
    try {
      await announcementService.renameAttachment(attachmentId, newName);
      setAttachments(prev => prev.map(a => a.attachment_id === attachmentId ? { ...a, display_name: newName } : a));
    } catch (err) {
      console.error("Failed to rename attachment:", err);
    }
  };

  const handleCancelUpload = (localId: string) => {
    setUploads(prev => prev.filter(u => u.localId !== localId));
  };

  const isTranslated = !!(translatedTitle || translatedDescription || translatedResolution);
  const isClosed = initialStatus === "Completed";
  const fieldDisabled = isClosed;
  const canDelete = form.status === "New";

  const tabs: { id: TabId; label: string; count?: number }[] = [
    { id: "info", label: t("tasks.detail.info") },
    { id: "comments", label: t("tasks.detail.comments"), count: commentCount || (inquiry?.comment_count ?? 0) },
    { id: "files", label: t("tasks.detail.files"), count: attachments.length },
  ];

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/40 z-40 transition-opacity duration-300 ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={`fixed top-0 right-0 h-full w-full max-w-lg bg-white shadow-2xl z-50 flex flex-col transition-transform duration-300 ease-in-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Toast */}
        {toast && (
          <div className="absolute top-4 left-4 right-4 z-10">
            <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
          </div>
        )}

        {/* Header */}
        <div className="flex items-start justify-between p-4 border-b border-gray-200 gap-3 shrink-0">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <span className="text-xs font-medium text-gray-400">#{inquiry?.ticket_id}</span>
              {form.status && (
                <Badge variant={statusVariant[form.status] || "default"}>
                  {getStatusLabel(form.status)}
                </Badge>
              )}
            </div>
            <h2 className="text-base font-semibold text-gray-900 line-clamp-2 leading-snug">
              {inquiry?.title || t("inquiry.title")}
            </h2>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {/* Translate button */}
            <div className="relative" ref={langPickerRef}>
              <button
                type="button"
                onClick={() => {
                  if (isTranslated) handleShowOriginal();
                  else setLangPickerOpen(prev => !prev);
                }}
                disabled={isTranslating}
                title={isTranslated ? t("feed.show_original") : t("feed.translate")}
                className={`inline-flex items-center gap-1.5 px-2 py-1.5 text-xs font-medium rounded-lg transition-colors disabled:opacity-50 ${
                  isTranslated
                    ? "bg-indigo-100 text-indigo-700"
                    : "text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                }`}
              >
                {isTranslating ? (
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                  </svg>
                )}
                {activeLang && !isTranslating && (
                  <span className="font-semibold">{activeLang.toUpperCase()}</span>
                )}
              </button>
              {langPickerOpen && (
                <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-20 min-w-[180px] py-1 overflow-y-auto max-h-[50vh]">
                  {SUPPORTED_LANGUAGES.map(lang => (
                    <button
                      key={lang.code}
                      type="button"
                      onClick={() => handleTranslate(lang.code as LanguageCode)}
                      className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <span>{LANGUAGE_FLAGS[lang.code] || ""}</span>
                      <span>{LANGUAGE_NATIVE_NAMES[lang.code] || lang.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Save button */}
            {(!isClosed || isOfficer) && (
              <Button size="sm" loading={saving} onClick={handleSubmit}>
                {t("inquiry.actions.update")}
              </Button>
            )}

            {/* Close */}
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 shrink-0">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? "text-indigo-600 border-b-2 border-indigo-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <span className="flex items-center justify-center gap-1.5">
                {tab.label}
                {tab.count !== undefined && tab.count > 0 && (
                  <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full leading-none ${
                    activeTab === tab.id ? "bg-indigo-100 text-indigo-600" : "bg-gray-100 text-gray-500"
                  }`}>
                    {tab.count}
                  </span>
                )}
              </span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {!inquiry ? null : (
            <>
              {/* INFO TAB */}
              {activeTab === "info" && (
                <div className="space-y-4">
                  {/* Trainee / Company / Visa card */}
                  <div className="grid grid-cols-3 gap-2 p-3 bg-gray-50 rounded-lg border border-gray-100 text-xs">
                    <div>
                      <p className="text-gray-500 font-medium mb-0.5">{t("inquiry.footer.trainee")}</p>
                      <p className="text-gray-800">{inquiry.owner_name || "-"}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 font-medium mb-0.5">{t("inquiry.table.company")}</p>
                      <p className="text-gray-800">{inquiry.company_name_text || "N/A"}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 font-medium mb-0.5">{t("return_home.info.visa_type")}</p>
                      <p className="text-gray-800">{inquiry.visa_type || "-"}</p>
                    </div>
                  </div>

                  {/* Status — officer can change, user sees read-only */}
                  {isOfficer ? (
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700">{t("inquiry.status.title")}</label>
                      <select
                        name="status"
                        value={form.status || "New"}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        {STATUS_OPTIONS.map(s => (
                          <option key={s} value={s}>{getStatusLabel(s)}</option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700">{t("inquiry.status.title")}</label>
                      <p className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600">
                        {getStatusLabel(form.status || "")}
                      </p>
                    </div>
                  )}

                  {/* Issue Type */}
                  <div className="space-y-1">
                    <label className={`block text-sm font-medium ${fieldDisabled ? "text-gray-400" : "text-gray-700"}`}>
                      {t("inquiry.form.issue_type")}
                    </label>
                    {isOfficer && !fieldDisabled ? (
                      <select
                        name="type"
                        value={form.type}
                        onChange={handleChange}
                        disabled={fieldDisabled}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="">{t("inquiry.form.issue_type")}</option>
                        {issues.map(issue => (
                          <option key={issue.value} value={issue.value}>{issue.label}</option>
                        ))}
                      </select>
                    ) : (
                      <p className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600">
                        {inquiry.type_name || inquiry.type || "-"}
                      </p>
                    )}
                  </div>

                  {/* Subject */}
                  <div className="space-y-1">
                    <label className={`block text-sm font-medium ${fieldDisabled ? "text-gray-400" : "text-gray-700"}`}>
                      {t("inquiry.form.subject")}
                    </label>
                    {translatedTitle ? (
                      <>
                        <p className="px-3 py-2 text-sm text-gray-800 bg-indigo-50 border border-indigo-100 rounded-lg">{translatedTitle}</p>
                        <p className="text-[10px] text-indigo-400 italic">{t("feed.translated")}</p>
                      </>
                    ) : fieldDisabled ? (
                      <p className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600">{form.title || "-"}</p>
                    ) : (
                      <Input name="title" value={form.title} onChange={handleChange} required maxLength={100} disabled={fieldDisabled} />
                    )}
                  </div>

                  {/* Description */}
                  <div className="space-y-1">
                    <label className={`block text-sm font-medium ${fieldDisabled ? "text-gray-400" : "text-gray-700"}`}>
                      {t("inquiry.form.description")}
                    </label>
                    {translatedDescription ? (
                      <>
                        <p className="px-3 py-2 text-sm text-gray-800 bg-indigo-50 border border-indigo-100 rounded-lg whitespace-pre-wrap">{translatedDescription}</p>
                        <p className="text-[10px] text-indigo-400 italic">{t("feed.translated")}</p>
                      </>
                    ) : fieldDisabled ? (
                      <p className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600 whitespace-pre-wrap">{form.description || "-"}</p>
                    ) : (
                      <textarea
                        name="description"
                        value={form.description}
                        onChange={handleChange}
                        rows={3}
                        disabled={fieldDisabled}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                    )}
                  </div>

                  {/* Occur Date */}
                  <Input
                    label={t("inquiry.form.occur_date")}
                    name="occur_date"
                    type="date"
                    value={form.occur_date || ""}
                    onChange={handleChange}
                    disabled={fieldDisabled}
                  />

                  {/* Officer-only fields */}
                  {isOfficer && (
                    <>
                      {/* Assigned To */}
                      <div className="space-y-1">
                        <label className={`block text-sm font-medium ${fieldDisabled ? "text-gray-400" : "text-gray-700"}`}>
                          {t("inquiry.form.assignee")}
                        </label>
                        <select
                          name="assigned_to"
                          value={form.assigned_to || ""}
                          onChange={handleChange}
                          disabled={fieldDisabled}
                          className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                            fieldDisabled ? "bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed" : "border-gray-300"
                          }`}
                        >
                          <option value="">{t("inquiry.form.select_officer")}</option>
                          {officers.map(o => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </div>

                      {/* Watchers */}
                      <div className="space-y-1">
                        <label className={`block text-sm font-medium ${fieldDisabled ? "text-gray-400" : "text-gray-700"}`}>
                          {t("inquiry.form.watchers")}
                        </label>
                        {form.watcher.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mb-1.5">
                            {form.watcher.map(wId => {
                              const match = officers.find(o => o.value === wId);
                              return (
                                <span key={wId} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                                  {match?.label || wId}
                                  <button type="button" onClick={() => handleWatcherToggle(wId)} className="hover:text-indigo-900">&times;</button>
                                </span>
                              );
                            })}
                          </div>
                        )}
                        <div className="relative" ref={watcherDropdownRef}>
                          <input
                            type="text"
                            value={watcherSearch}
                            onChange={e => { setWatcherSearch(e.target.value); if (!watcherDropdownOpen) setWatcherDropdownOpen(true); }}
                            onFocus={() => { if (!fieldDisabled) setWatcherDropdownOpen(true); }}
                            disabled={fieldDisabled}
                            placeholder={t("inquiry.form.add_watchers")}
                            className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                              fieldDisabled ? "bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed" : "border-gray-300"
                            }`}
                          />
                          {watcherDropdownOpen && !fieldDisabled && (
                            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-40 overflow-y-auto">
                              {officers
                                .filter(o => o.label?.toLowerCase().includes(watcherSearch.toLowerCase()))
                                .map(o => {
                                  const isSelected = form.watcher.includes(o.value);
                                  return (
                                    <button
                                      key={o.value}
                                      type="button"
                                      onClick={() => { handleWatcherToggle(o.value); setWatcherSearch(""); }}
                                      className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors ${isSelected ? "bg-indigo-50 text-indigo-700" : "text-gray-700 hover:bg-gray-50"}`}
                                    >
                                      <span className={`w-4 h-4 rounded border flex items-center justify-center text-[10px] shrink-0 ${isSelected ? "bg-indigo-600 border-indigo-600 text-white" : "border-gray-300"}`}>
                                        {isSelected && "✓"}
                                      </span>
                                      {o.label}
                                    </button>
                                  );
                                })}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* High Priority */}
                      <label className={`flex items-center gap-2 text-sm ${fieldDisabled ? "text-gray-400 cursor-not-allowed" : "text-gray-700"}`}>
                        <input
                          type="checkbox"
                          name="high_pri"
                          checked={form.high_pri}
                          onChange={handleChange}
                          disabled={fieldDisabled}
                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:cursor-not-allowed"
                        />
                        {t("inquiry.form.priority")}
                      </label>
                    </>
                  )}

                  {/* Resolution + Close Date (when Completed) */}
                  {form.status === "Completed" && (
                    <>
                      <div className="space-y-1">
                        <label className="block text-sm font-medium text-gray-700">{t("inquiry.form.resolution")}</label>
                        {translatedResolution ? (
                          <>
                            <p className="px-3 py-2 text-sm text-gray-800 bg-indigo-50 border border-indigo-100 rounded-lg whitespace-pre-wrap">{translatedResolution}</p>
                            <p className="text-[10px] text-indigo-400 italic">{t("feed.translated")}</p>
                          </>
                        ) : (
                          <textarea
                            name="resolution"
                            value={form.resolution || ""}
                            onChange={handleChange}
                            rows={3}
                            required
                            disabled={fieldDisabled}
                            placeholder={t("inquiry.form.resolution_placeholder")}
                            className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                              fieldDisabled ? "bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed" : "border-gray-300"
                            }`}
                          />
                        )}
                      </div>
                      <Input
                        label={`${t("inquiry.form.close_date")} *`}
                        name="closed_dt"
                        type="date"
                        value={form.closed_dt || ""}
                        onChange={handleChange}
                        required
                        disabled={fieldDisabled}
                      />
                    </>
                  )}

                  {/* Audit fields */}
                  <div className="pt-3 border-t border-gray-100">
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <span className="text-gray-500">{t("inquiry.footer.created_by")}:</span>
                        <p className="text-gray-800">{inquiry.opened_by_name || "-"}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Open Date:</span>
                        <p className="text-gray-800">{inquiry.open_dt ? formatDate(inquiry.open_dt) : "-"}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">{t("inquiry.footer.updated_by")}:</span>
                        <p className="text-gray-800">{inquiry.last_updated_by_name || "-"}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">{t("inquiry.footer.last_updated")}:</span>
                        <p className="text-gray-800">{inquiry.last_update_dttm ? formatDateTime(inquiry.last_update_dttm) : "-"}</p>
                      </div>
                    </div>
                  </div>

                  {error && <p className="text-sm text-red-600">{error}</p>}

                  {/* Delete */}
                  {canDelete && (
                    <div className="pt-2 border-t border-gray-200">
                      {confirmDelete ? (
                        <div className="flex items-center gap-2">
                          <p className="text-xs text-red-600 flex-1">{t("tasks.detail.confirm_delete")}</p>
                          <Button variant="danger" size="sm" loading={deleting} onClick={handleDelete}>
                            {deleting ? t("tasks.detail.deleting") : t("common.confirm", { defaultValue: "Confirm" })}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(false)}>
                            {t("tasks.form.cancel")}
                          </Button>
                        </div>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setConfirmDelete(true)}
                          className="text-red-600 hover:bg-red-50"
                        >
                          {t("common.delete")}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* COMMENTS TAB */}
              {activeTab === "comments" && (
                <CommentSection
                  announcementId={inquiry.ticket_id}
                  relationType="inquiries"
                  currentUserId={userData?.id ?? ""}
                  currentUserName={formatDisplayName(userData?.lastName, userData?.firstName, userData?.middleName)}
                  isOfficer={isOfficer}
                  onCountChange={setCommentCount}
                />
              )}

              {/* FILES TAB */}
              {activeTab === "files" && (
                <div className="space-y-3">
                  <AttachmentDropZone
                    onFilesSelected={handleFilesSelected}
                    accept=".pdf,.jpg,.jpeg,.png,.gif"
                    disabled={uploads.some(u => u.status === "uploading")}
                  />
                  <AttachmentList
                    attachments={attachments}
                    uploads={uploads}
                    onDelete={handleAttachmentDelete}
                    onRename={handleAttachmentRename}
                    onCancelUpload={handleCancelUpload}
                  />
                  {attachments.length === 0 && uploads.length === 0 && (
                    <p className="text-xs text-gray-400 italic text-center py-2">{t("inquiry.no_results")}</p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};
